import React, {Fragment, useState} from 'react';
import {translate} from "react-i18next";
import {connect} from "react-redux";
import {compose} from "redux";
import {Button, Col, Form, Icon, Input, message, Modal, Row} from "antd";
import {
  BUTTON_API_LINK,
  BUTTON_CLONE,
  BUTTON_DELETE,
  BUTTON_DETAIL,
  BUTTON_DOWNLOAD,
  BUTTON_INFO_PAGE,
  BUTTON_INFO_PAGE_LINK,
  BUTTON_PUBLISH,
  BUTTON_UNPUBLISH
} from "../../../styles/buttons";
import EnhancedModal from "../../../components/enhanced-modal";
import {getNodes} from "../../../utils/tree";
import copy from "copy-to-clipboard";
import {
  CATALOG_TARGET_ID,
  DATAFLOW_TARGET_ID,
  DATASET_IDENTIFIER,
  getDbIdAnnotationFromAnnotations,
  getMetadataApiDcatReportUrl,
  getMetadataApiReportUrl,
  getReportHtmlPageUrl,
  isAttributeEmpty,
  isAttributeValid,
  REPORT_ID_ANNOTATION_ID,
  REPORT_STATE_DRAFT,
  REPORT_STATE_NOT_PUBLISHED,
  REPORT_STATE_PUBLISHED
} from "../../../utils/referenceMetadata";
import InfiniteScrollTable from "../../../components/infinite-scroll-table";
import {EXPORT_REPORT_FORMAT_XML, getImportReportResultTranslation, REPORT_DETAILS_WIZARD_STEP_FIRST, REPORT_DETAILS_WIZARD_STEP_SECOND} from "./constants";
import {getArtefactTripletFromUrn} from "../../../utils/sdmxJson";
import {DataLanguageConsumer} from "../../../contexts/DataLanguage";
import HtmlTemplateViewer from "../../../components/html-template-viewer";
import {GUTTER_MD, GUTTER_SM, MODAL_WIDTH_LG, MODAL_WIDTH_MD, MODAL_WIDTH_SM, SPAN_FULL, SPAN_ONE_THIRD, SPAN_TWO_THIRDS} from "../../../styles/constants";
import {isIDValidWithHelp} from "../../../utils/artefactValidators";
import PerfInput from "../../../components/perf-input";
import FileInput from "../../../components/file-input";
import ReportExportForm from "./ReportExportForm";

const $ = window.jQuery;

const mapStateToProps = state => ({
  nodeId: state.app.endpointId,
  nodes: state.config.nodes,
  appLang: state.app.language,
  dataLanguages: state.config.dataManagement.dataLanguages
});

export const isReportIdWarningVisible = (newId, reports) => !!(reports || []).find(({id}) => id === newId);

const formItemLayout = {
  labelCol: {span: SPAN_ONE_THIRD},
  wrapperCol: {span: SPAN_TWO_THIRDS}
};

const ReportList = ({
                      t,
                      form,
                      nodeId,
                      nodes,
                      appLang,
                      dataLanguages,
                      isDcat,
                      ReportDetailWrapper,
                      msPermission,
                      metadataSet,
                      reports,
                      report,
                      reportAnnotations,
                      isReportVisible,
                      reportStructure,
                      step,
                      reportId,
                      target,
                      identifiableTargets,
                      reportHtmlPageUrl,
                      selectedAttribute,
                      onReportCreate,
                      onReportDelete,
                      onReportShow,
                      onReportHide,
                      onReportSubmit,
                      onStepSet,
                      onReportStateSubmit,
                      onHtmlPageShow,
                      onHtmlPageHide,
                      onReportDownload,
                      ownedMetadataflows,
                      ownedDataflows,
                      onReportCloneSubmit,
                      uploadedFileHash,
                      uploadedReports,
                      importedReports,
                      onReportsFileUpload,
                      onReportsClear,
                      onReportsSubmit
                    }) => {

  const [clonedReportId, setClonedReportId] = useState(null);
  const newReportId = form.getFieldValue("newReportId");

  const [isImportVisible, setImportVisibility] = useState(false);
  const [importedFile, setImportedFile] = useState(null);

  const [exportedReportId, setExportedReportId] = useState(null);
  const [exportedReportFormat, setExportedReportFormat] = useState(EXPORT_REPORT_FORMAT_XML);

  const handleExportShow = reportId => {
    setExportedReportId(reportId);
  };

  const handleExportHide = () => {
    setExportedReportId(null);
    setExportedReportFormat(EXPORT_REPORT_FORMAT_XML);
  };

  const handleExportChange = format => {
    setExportedReportFormat(format);
  };

  const handleExportSubmit = () => {
    onReportDownload(metadataSet.id, exportedReportId, `${exportedReportId}.${exportedReportFormat}`, exportedReportFormat);
    handleExportHide();
  };

  const handleCloneShow = id => {
    setClonedReportId(id);
  };

  const handleCloneHide = () => {
    setClonedReportId(null);
  };

  const handleCloneSubmit = () => {
    onReportCloneSubmit(metadataSet.id, clonedReportId, newReportId);
    setClonedReportId(null);
  };

  const handleImportShow = () => {
    setImportVisibility(true);
  };

  const handleImportHide = () => {
    setImportVisibility(false);
    setImportedFile(null);
  };

  const handleImportSubmit = () => {
    if (isDcat) {
      onReportsFileUpload(metadataSet.id, importedFile, ownedDataflows, ownedMetadataflows);
    } else {
      onReportsFileUpload(metadataSet.id, importedFile);
    }
  };

  return (
    <DataLanguageConsumer>
      {dataLanguage => {
        const lang = dataLanguage || appLang;

        const connectedNode = nodes.find(node => node.general.id === nodeId);
        const annotationsConfig = connectedNode.annotations;
        const metadataApiBaseUrl = connectedNode.endpoint.metadataBaseURL;

        const actions = [
          ({hasPermission: reportPermission}) => ({
            ...BUTTON_DETAIL,
            title: reportPermission === false
              ? t("commons.actions.detailReadOnly.title")
              : t("commons.actions.detail.title"),
            onClick: report => {
              let dataflowTriplet = undefined;
              const referenceValue = report.target.referenceValues.find(({id}) => id === DATASET_IDENTIFIER);
              if (report.target.id === DATAFLOW_TARGET_ID && referenceValue !== undefined) {
                dataflowTriplet = getArtefactTripletFromUrn(referenceValue.object);
              }
              onReportShow(metadataSet.id, report.id, report, dataflowTriplet);
            }
          }),
          ({isPublished}) => ({
            ...BUTTON_INFO_PAGE,
            title: t("scenes.referenceMetadata.commons.reportList.actions.navigateHtmlPage.title"),
            onClick: ({id}) => onHtmlPageShow(getReportHtmlPageUrl(nodeId, metadataSet.id, id, lang, metadataApiBaseUrl, isDcat)),
            disabled: (isPublished !== REPORT_STATE_PUBLISHED || !metadataApiBaseUrl)
          }),
          ({isPublished}) => ({
            ...BUTTON_INFO_PAGE_LINK,
            title: t("scenes.referenceMetadata.commons.reportList.actions.copyHtmlUrl.title"),
            onClick: ({id}) => {
              copy(getReportHtmlPageUrl(nodeId, metadataSet.id, id, lang, metadataApiBaseUrl, isDcat));
              return (
                message.info(t("scenes.referenceMetadata.commons.reportList.actions.copyHtmlUrl.message"))
              )
            },
            disabled: (isPublished !== REPORT_STATE_PUBLISHED || !metadataApiBaseUrl)
          }),
          ({isPublished}) => ({
            ...BUTTON_API_LINK,
            title: t("scenes.referenceMetadata.commons.reportList.actions.copyCkanUrl.title"),
            onClick: ({id, target}) => {
              copy((metadataSet && isDcat && target.id === DATAFLOW_TARGET_ID)
                ? getMetadataApiDcatReportUrl(metadataApiBaseUrl, metadataSet.id, id, lang)
                : getMetadataApiReportUrl(metadataApiBaseUrl, metadataSet.id, id)
              );
              return (
                message.info(t("scenes.referenceMetadata.commons.reportList.actions.copyCkanUrl.message"))
              )
            },
            disabled: (isPublished !== REPORT_STATE_PUBLISHED || !metadataApiBaseUrl)
          }),
          () => ({
            ...BUTTON_DOWNLOAD,
            title: t("commons.actions.download.title"),
            onClick: ({id}) => handleExportShow(id)
          })
        ];

        if (onReportCloneSubmit) {
          actions.push(({hasPermission: reportPermission, target}) => ({
            ...BUTTON_CLONE,
            title: t("commons.actions.clone.title"),
            onClick: ({id}) => handleCloneShow(id),
            disabled: isDcat
              ? (!reportPermission || !msPermission || target.id === CATALOG_TARGET_ID)
              : (!reportPermission || !msPermission)
          }));
        }

        actions.push(({hasPermission: reportPermission, target}) => ({
          ...BUTTON_DELETE,
          title: t("commons.actions.delete.title"),
          onClick: report => Modal.confirm({
            title: t('scenes.referenceMetadata.commons.reportList.modals.confirms.delete.title', {id: report.id}),
            onOk() {
              onReportDelete(getDbIdAnnotationFromAnnotations(report, REPORT_ID_ANNOTATION_ID).text)
            },
            cancelText: t('commons.buttons.cancel.title')
          }),
          disabled: isDcat
            ? (!reportPermission || !msPermission || (target.id === CATALOG_TARGET_ID && reports !== null && reports.length > 1))
            : (!reportPermission || !msPermission)
        }));

        return (
          <Fragment>
            <InfiniteScrollTable
              data={reports}
              getRowKey={({id}) => id}
              columns={[
                {
                  title: t('scenes.referenceMetadata.commons.reportList.columns.id.title'),
                  dataIndex: 'id',
                  minWidth: 100
                },
                {
                  title: t('scenes.referenceMetadata.commons.reportList.columns.structure.title'),
                  dataIndex: 'targetId',
                  minWidth: 100
                },
                {
                  title: t('scenes.referenceMetadata.commons.reportList.columns.targetIdentifier.title'),
                  dataIndex: 'targetReferenceValues',
                  minWidth: isDcat ? 100 : 500
                },
                {
                  title: t('data.artefact.isPublished.shortLabel'),
                  dataIndex: "isPublished",
                  width: 30,
                  withValuesFilter: true,
                  render: isPublished => isPublished === REPORT_STATE_PUBLISHED
                    ? <Icon type="check"/>
                    : isPublished === REPORT_STATE_DRAFT
                      ? <Icon type="close"/>
                      : null,
                  renderText: isPublished => isPublished === REPORT_STATE_PUBLISHED
                    ? t('data.artefact.isPublished.values.published')
                    : isPublished === REPORT_STATE_DRAFT
                      ? t('data.artefact.isPublished.values.draft')
                      : t('data.artefact.isPublished.values.notPublished')
                }
              ]}
              actions={[
                ({hasPermission: reportPermission, isPublished}) => ({
                  ...BUTTON_PUBLISH,
                  title: t("scenes.referenceMetadata.commons.reportList.actions.publish.title"),
                  onClick: ({id}) => Modal.confirm({
                    title: t('scenes.referenceMetadata.commons.reportList.modals.confirms.publish.title', {id: metadataSet.id}),
                    onOk() {
                      onReportStateSubmit(metadataSet.id, id, REPORT_STATE_PUBLISHED)
                    },
                    cancelText: t('commons.buttons.cancel.title')
                  }),
                  disabled: (isPublished === REPORT_STATE_DRAFT || !reportPermission || !msPermission)
                }),
                ...actions
              ]}
              altActions={[
                ({hasPermission: reportPermission, isPublished}) => ({
                  ...BUTTON_UNPUBLISH,
                  title: t("scenes.referenceMetadata.commons.reportList.actions.unpublish.title"),
                  onClick: ({id}) => Modal.confirm({
                    title: t('scenes.referenceMetadata.commons.reportList.modals.confirms.unpublish.title', {id: metadataSet.id}),
                    onOk() {
                      onReportStateSubmit(metadataSet.id, id, REPORT_STATE_NOT_PUBLISHED)
                    },
                    cancelText: t('commons.buttons.cancel.title')
                  }),
                  disabled: (isPublished === REPORT_STATE_DRAFT || !reportPermission || !msPermission)
                }),
                ...actions
              ]}
              getIsAltRow={({isPublished}) => isPublished === REPORT_STATE_PUBLISHED}
              rightActions={
                <Row type="flex" gutter={GUTTER_SM}>
                  {onReportsFileUpload && (
                    <Col>
                      <Button
                        icon="upload"
                        onClick={handleImportShow}
                        disabled={!msPermission}
                      >
                        {t('scenes.referenceMetadata.commons.reportList.actions.import.title')}
                      </Button>
                    </Col>
                  )}
                  <Col>
                    <Button
                      type="primary"
                      icon="plus"
                      onClick={onReportCreate}
                      disabled={!msPermission}
                    >
                      {t('scenes.referenceMetadata.commons.reportList.actions.create.title')}
                    </Button>
                  </Col>
                </Row>
              }
            />
            <EnhancedModal
              visible={isReportVisible}
              onCancel={onReportHide}
              title={t('scenes.referenceMetadata.commons.reportList.modals.report.title')}
              fullscreen
              withDataLanguageSelector
              footer={
                <div>
                  <Button onClick={onReportHide}>{t('commons.buttons.close.title')}</Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      if (step === REPORT_DETAILS_WIZARD_STEP_FIRST) {
                        onStepSet(REPORT_DETAILS_WIZARD_STEP_SECOND, dataLanguages, t);

                      } else {
                        if (!selectedAttribute || isAttributeValid(selectedAttribute)) {
                          const emptyNodes =
                            getNodes(reportStructure, "metadataAttributes", node => isAttributeEmpty(node))
                              .map(({name}) => name);

                          if (emptyNodes && emptyNodes.length > 0) {
                            Modal.confirm({
                              title: t("scenes.referenceMetadata.commons.reportList.modals.warning.emptyAttribute.title"),
                              content: (
                                <Fragment>
                                  {t("scenes.referenceMetadata.commons.reportList.modals.warning.emptyAttribute.content")}:
                                  {emptyNodes.map((node, index) =>
                                    <div key={index}>
                                      - {node}
                                    </div>
                                  )}
                                </Fragment>
                              ),
                              onOk() {
                                onReportSubmit(metadataSet, report, reportAnnotations, reportStructure, reportId, target, identifiableTargets, annotationsConfig, true);
                              },
                              okText: t("scenes.referenceMetadata.commons.reportList.modals.warning.emptyAttribute.saveDraft.title")
                            });

                          } else {
                            onReportSubmit(metadataSet, report, reportAnnotations, reportStructure, reportId, target, identifiableTargets, annotationsConfig, false);
                          }

                        } else if (selectedAttribute && !isAttributeValid(selectedAttribute)) {
                          $(".report-structure-tree__waring").css({color: "#FF0000"});
                        }
                      }
                    }}
                    disabled={step === REPORT_DETAILS_WIZARD_STEP_FIRST
                      ? (
                        reportId === null ||
                        reportId.length === 0 ||
                        (report === null && isReportIdWarningVisible(reportId, reports)) ||
                        target === null || identifiableTargets.find(el => !el.value || el.value.length === 0)
                      )
                      : (report && (report.isPublished === REPORT_STATE_PUBLISHED || !report.hasPermission))
                    }
                  >
                    {
                      step === REPORT_DETAILS_WIZARD_STEP_FIRST
                        ? t('commons.buttons.goForward.title')
                        : t('commons.buttons.save.title')
                    }
                  </Button>
                </div>
              }
            >
              <ReportDetailWrapper
                msPermission={msPermission}
                ownedMetadataflows={ownedMetadataflows}
              />
            </EnhancedModal>
            <HtmlTemplateViewer
              title={t('scenes.referenceMetadata.commons.modals.htmlPage.title')}
              htmlPageUrl={reportHtmlPageUrl}
              onClose={onHtmlPageHide}
            />
            <EnhancedModal
              visible={clonedReportId !== null}
              title={t('scenes.referenceMetadata.commons.modals.cloneReport.title')}
              onCancel={handleCloneHide}
              footer={
                <div>
                  <Button onClick={handleCloneHide}>
                    {t('commons.buttons.close.title')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      if (
                        newReportId !== null &&
                        newReportId !== undefined &&
                        !(reports || []).find(({id}) => id === newReportId) &&
                        isIDValidWithHelp(t, newReportId).valid
                      ) {
                        handleCloneSubmit();
                      }
                    }}
                  >
                    {t('commons.buttons.confirm.title')}
                  </Button>
                </div>
              }
              width={MODAL_WIDTH_SM}
            >
              <Form.Item
                label={t("scenes.referenceMetadata.commons.modals.cloneReport.fileds.id.label")}
                className="form-item-required"
                hasFeedback
                validateStatus={(newReportId && ((reports || []).find(({id}) => id === newReportId) || !isIDValidWithHelp(t, newReportId).valid))
                  ? "warning"
                  : null
                }
                help={newReportId
                  ? (reports || []).find(({id}) => id === newReportId)
                    ? t("scenes.referenceMetadata.commons.modals.cloneReport.fileds.id.alreadyUsed.help")
                    : (isIDValidWithHelp(t, newReportId).help || undefined)
                  : undefined
                }
                {...formItemLayout}
              >
                {form.getFieldDecorator("newReportId", {
                  getValueFromEvent: val => (val || "").toUpperCase()
                })(
                  <PerfInput
                    Component={Input}
                    title={newReportId}
                    style={{textTransform: "uppercase"}}
                  />
                )}
              </Form.Item>
            </EnhancedModal>
            <EnhancedModal
              visible={isImportVisible}
              onCancel={handleImportHide}
              footer={
                <Button onClick={handleImportHide}>
                  {t('commons.buttons.close.title')}
                </Button>
              }
              width={MODAL_WIDTH_MD}
              title={t('scenes.referenceMetadata.commons.modals.importReport.title')}
            >
              <Col span={SPAN_FULL}>
                <Row type="flex" align="middle" gutter={GUTTER_MD} justify="center">
                  <Col>
                    {t('scenes.referenceMetadata.commons.modals.importReport.selectFile.label')}:
                  </Col>
                  <Col style={{width: 320}}>
                    <FileInput
                      value={importedFile}
                      onChange={setImportedFile}
                      accept={".json"}
                    />
                  </Col>
                  <Col>
                    <Button
                      icon="upload"
                      type="primary"
                      onClick={handleImportSubmit}
                      disabled={importedFile === null}
                    >
                      {t('scenes.referenceMetadata.commons.modals.importReport.uploadFile.label')}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </EnhancedModal>
            <EnhancedModal
              visible={uploadedReports !== null}
              onCancel={onReportsClear}
              footer={
                <Button onClick={onReportsClear}>
                  {t('commons.buttons.close.title')}
                </Button>
              }
              width={MODAL_WIDTH_LG}
              title={t('scenes.referenceMetadata.commons.modals.selectReport.title')}
            >
              <InfiniteScrollTable
                data={uploadedReports}
                getRowKey={({Id}) => Id}
                columns={[
                  {
                    title: t('scenes.referenceMetadata.commons.reportList.columns.id.title'),
                    dataIndex: 'Id'
                  },
                  {
                    title: t('scenes.referenceMetadata.commons.reportList.columns.structure.title'),
                    dataIndex: 'targetId'
                  },
                  {
                    title: t('scenes.referenceMetadata.commons.reportList.columns.targetIdentifier.title'),
                    dataIndex: 'targetReferenceValues'
                  },
                  {
                    title: t('scenes.referenceMetadata.commons.reportList.columns.importMessage.title'),
                    dataIndex: 'Message',
                    render: Message => getImportReportResultTranslation(Message, t),
                    renderText: Message => getImportReportResultTranslation(Message, t)
                  }
                ]}
                tableActions={[{
                  icon: "check",
                  title: t('scenes.referenceMetadata.commons.reportList.actions.import.title'),
                  onClick: selectedArr => {
                    setImportVisibility(false);
                    setImportedFile(null);
                    onReportsSubmit(metadataSet.id, uploadedFileHash, selectedArr);
                  }
                }]}
                getIsDisabledRow={({importable}) => !importable}
              />
            </EnhancedModal>
            <EnhancedModal
              visible={importedReports !== null}
              onCancel={onReportsClear}
              footer={
                <Button onClick={onReportsClear}>
                  {t('commons.buttons.close.title')}
                </Button>
              }
              width={MODAL_WIDTH_LG}
              title={t('scenes.referenceMetadata.commons.modals.importResults.title')}
            >
              <InfiniteScrollTable
                data={importedReports}
                getRowKey={({Id}) => Id}
                columns={[
                  {
                    title: t('scenes.referenceMetadata.commons.reportList.columns.id.title'),
                    dataIndex: 'Id'
                  },
                  {
                    title: t('scenes.referenceMetadata.commons.reportList.columns.result.title'),
                    dataIndex: 'Result',
                    width: 30,
                    withValuesFilter: true,
                    render: Result => Result === true
                      ? <Icon type="check-circle" theme="filled" style={{color: 'green'}}/>
                      : <Icon type="close-circle" theme="filled" style={{color: 'red'}}/>,
                    renderText: Result => Result === true
                      ? t('scenes.referenceMetadata.commons.reportList.columns.result.values.true')
                      : t('scenes.referenceMetadata.commons.reportList.columns.result.values.false')
                  },
                  {
                    title: t('scenes.referenceMetadata.commons.reportList.columns.importMessage.title'),
                    dataIndex: 'Message',
                    render: Message => getImportReportResultTranslation(Message, t),
                    renderText: Message => getImportReportResultTranslation(Message, t)
                  }
                ]}
              />
            </EnhancedModal>
            <EnhancedModal
              visible={exportedReportId !== null && exportedReportId !== undefined}
              onCancel={handleExportHide}
              footer={
                <div>
                  <Button onClick={handleExportHide}>
                    {t('commons.buttons.close.title')}
                  </Button>
                  <Button type="primary" onClick={handleExportSubmit}>
                    {t('commons.buttons.download.title')}
                  </Button>
                </div>
              }
              width={MODAL_WIDTH_SM}
              title={t('scenes.referenceMetadata.commons.modals.reportExport.title', {id: exportedReportId})}
            >
              <ReportExportForm
                format={exportedReportFormat}
                onFormatChange={handleExportChange}
              />
            </EnhancedModal>
          </Fragment>
        )
      }}
    </DataLanguageConsumer>
  );
};

export default compose(
  translate(),
  connect(mapStateToProps),
  Form.create()
)(ReportList);
